import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams, useSearch } from 'wouter'
import queryString from 'query-string'
import { useDocumentState } from '@/state'
import { isLine } from './components/Annotations/utils'

export const useDocumentPageParams = () => {
  const params = useParams<{
    projectId: string
    documentId: string
  }>()
  const search = useSearch()
  const queryParams = queryString.parse(search) as {
    cv?: string // cad version
    dv?: string // document version
    comment_id?: string // comment id
  }
  return { ...params, ...queryParams }
}

export const useComments = () => {
  const commentsEnabled = useDocumentState((state) => state.commentsEnabled)
  const { comment_id: commentId } = useDocumentPageParams()
  return commentsEnabled || !!commentId
}

export const useDraggableElements = () => {
  const triggerLocation = useDocumentState((state) => state.triggerLocation)
  const clearNewElementData = useDocumentState(
    (state) => state.clearNewElementData,
  )
  const setLinePoint = useDocumentState((state) => state.setLinePoint)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedPage = useDocumentState((state) => state.currentPageInView)
  const menuRefPosition = useDocumentState((state) => state.menuRefPosition)
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const setCanDropElement = useDocumentState((state) => state.setCanDropElement)
  const zoom = useDocumentState((state) => state.zoom)
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const animationRef = useRef<number | null>(null)

  const alignPlaceholder = useCallback(
    (x: number, y: number) => {
      const reference = menuRefPosition || { x: 0, y: 0 }
      const dataType = newElementData?.dataType
      const type =
        dataType === 'annotation' ? newElementData?.attributes.type : dataType
      const iconPlaceholder = document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const { width, height } =
        iconPlaceholder.getBoundingClientRect() as DOMRect
      if (!iconPlaceholder) return

      iconPlaceholder.style.transform = `translateY(${
        y - reference.y - height / 2
      }px)`
      iconPlaceholder.style.transform += `translateX(${
        x - reference.x - width / 2
      }px)`
      iconPlaceholder.style.transform += `scale(${zoomPercentage})`
      iconPlaceholder.style.transformOrigin = '0 0'
    },
    [menuRefPosition, newElementData, zoomPercentage],
  )

  const calculateCanDrop = useCallback(
    ({ mousePosition }: { mousePosition: { x: number; y: number } }) => {
      const dataType = newElementData?.dataType
      const type =
        dataType === 'annotation' ? newElementData?.attributes.type : dataType
      const iconPlaceholder = document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const container = document.getElementById(
        `droppable-container--${selectedPage}`,
      ) as HTMLElement

      if (!container || !iconPlaceholder) return
      const {
        left: containerLeft,
        top: containerTop,
        right: containerRight,
        bottom: containerBottom,
      } = container.getBoundingClientRect()
      const { width: placeholderWidth, height: placeholderHeight } =
        iconPlaceholder.getBoundingClientRect()
      const widthOffset = !isLine(type || '') ? placeholderWidth / 2 : 0
      const heightOffset = !isLine(type || '') ? placeholderHeight / 2 : 0

      const canDrop =
        mousePosition.x - widthOffset >= containerLeft &&
        mousePosition.x + widthOffset <= containerRight &&
        mousePosition.y - heightOffset >= containerTop &&
        mousePosition.y + heightOffset <= containerBottom &&
        selectedPage === newElementData?.documentPageId

      if (canDrop !== canDropElement) setCanDropElement(canDrop)
    },
    [selectedPage, canDropElement, newElementData, setCanDropElement],
  )

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      alignPlaceholder(event.clientX, event.clientY)
      calculateCanDrop({
        mousePosition: { x: event.clientX, y: event.clientY },
      })
      if (newElementData?.attributes.startPoint) {
        if (animationRef.current) cancelAnimationFrame(animationRef.current)

        animationRef.current = requestAnimationFrame(() => {
          const container = document.getElementById(
            `droppable-container--${selectedPage}`,
          ) as HTMLElement
          const { x, y } = container.getBoundingClientRect()

          setLinePoint({
            type: 'end',
            position: {
              x: event.clientX / zoomPercentage - x / zoomPercentage,
              y: event.clientY / zoomPercentage - y / zoomPercentage,
            },
          })
        })
      }
    },
    [
      newElementData?.attributes.startPoint,
      selectedPage,
      zoomPercentage,
      setLinePoint,
      alignPlaceholder,
      calculateCanDrop,
    ],
  )

  const unselectElement = useCallback(() => {
    setSelectedElement(null)
  }, [setSelectedElement])

  useEffect(() => {
    if (selectedElement) {
      document.addEventListener('click', unselectElement)
    } else {
      document.removeEventListener('click', unselectElement)
    }

    return () => {
      document.removeEventListener('click', unselectElement)
    }
  }, [unselectElement, selectedElement])

  const onStopAddingNewElement = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearNewElementData()
    },
    [clearNewElementData],
  )

  const addNewElementListeners = useCallback(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('keydown', onStopAddingNewElement)
  }, [onMouseMove, onStopAddingNewElement])

  const removeNewElementListeners = useCallback(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('keydown', onStopAddingNewElement)
    if (animationRef.current) cancelAnimationFrame(animationRef.current)
  }, [onMouseMove, onStopAddingNewElement])

  useEffect(() => {
    if (isAddingElement && triggerLocation) {
      addNewElementListeners()
    } else if (!isAddingElement) {
      removeNewElementListeners()
    }

    return () => removeNewElementListeners()
  }, [
    isAddingElement,
    triggerLocation,
    addNewElementListeners,
    removeNewElementListeners,
  ])

  useEffect(() => {
    if (isAddingElement && triggerLocation) {
      alignPlaceholder(triggerLocation.x, triggerLocation.y)
    }
  }, [isAddingElement, triggerLocation])
}
